/**
 * La résolution de nuit — SPEC-MOTEUR §2.4, dans son **ordre strict** :
 *
 * ```
 * 1. énergie +80, SM +3            (débités par la table des coûts §2.2, avant ce hook)
 *    ⟦A-2⟧ + supplément de SM si une pause annoncée est en cours (SM ×1,5)
 * 2. rente evergreen, pro rata Δh/112                                            (§6.3)
 * 3. désabonnements f(Fidélité), deux pools, pro rata Δh/112                      (§4.7)
 * 4. decay de momentum du silence — ⟦A-2⟧ GELÉ pendant une pause annoncée (cap 21 j)
 * 5. tendances : phases, rotations, expiration de la fenêtre d'opportunité          (§8)
 * 6. strikes expirés, thèmes épuisés
 * ```
 *
 * `nights`, `counters.subs_at_last_sleep` et `counters.last_sleep_h` sont posés par la file de
 * résolution elle-même (`actions.ts`, §2.3 pas 6) : **on ne les duplique pas ici**.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`. Le contenu
 * arrive en paramètre (arbitrage A25), le temps est `state.clock_h` en heures **éveillées**.
 *
 * Convention de mutation (celle de `curves.ts`) : les fonctions de ce fichier **mutent** l'état
 * qu'on leur passe. Elles sont appelées depuis le hook `nuit(ctx, deltaH)`, dont le `ctx.state`
 * est déjà le clone de travail de la file §2.3 — jamais l'état d'entrée de `resolve`.
 */
import { actionCost, appliquerClamps, bandeAlignement } from "./actions.js";
import { consommerNuitProchaine, contenuBoutique } from "./boutique.js";
import { H_DEMI_VIE_EVERGREEN, H_JOUR, H_SILENCE_TOLERE, H_THEME_EPUISE, prorataHebdo, } from "./clock.js";
import { RPM_LONGUE, RPM_SHORT, estMonetise } from "./curves.js";
import { collectModifiers, } from "./effects.js";
import { retention } from "./progress.js";
import { avancerTendances, expirerFenetreOpportunite } from "./trends.js";
// ─────────────────────────────────────────────────────────────────────────────
// Constantes normatives
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦A-2⟧ Récupération de SM pendant une pause annoncée : **×1,5** (avenant A-2 point 2).
 * Le +3 de base est déjà débité par la table des coûts (§2.2) au pas 3 de la file : ce hook
 * n'ajoute que le **supplément**, lu depuis `actionCost` pour n'avoir qu'une source de vérité.
 */
export const MULT_SM_PAUSE_ANNONCEE = 1.5;
/**
 * ⟦A-2⟧ Plafond du gel de silence : **21 jours simulés** (avenant A-2 point 1) — « au-delà, le
 * silence redevient du silence ».
 */
export const H_PAUSE_PROTEGEE_MAX = 21 * H_JOUR; // 336 h
/** Momentum perdu par jour entier de silence au-delà des 3 tolérés (§5.1, PLAN §4.2). */
export const MOMENTUM_DECAY_PAR_JOUR = 0.05;
/** 3 jours de silence tolérés = `H_SILENCE_TOLERE` (48 h) / `H_JOUR`. */
export const JOURS_SILENCE_TOLERES = H_SILENCE_TOLERE / H_JOUR;
/** Taux de désabonnement hebdomadaire des fidèles : `0,0005 + 0,004 × (1 − Fid/100)` (§4.7). */
export const TAUX_DESABO_BASE = 0.0005;
export const TAUX_DESABO_PENTE = 0.004;
/** Les volatils se désabonnent **4 fois plus vite** (§4.7). */
export const FACTEUR_VOLATILS = 4;
/** Rente evergreen : base **2,5 %** du pic par semaine (§6.3). */
export const RENTE_HEBDO_BASE = 0.025;
/** Vidéo rangée en série : rente **×1,2** (§6.3, PLAN §4.6 « +20 % de rente croisée »). */
export const RENTE_MULT_SERIE = 1.2;
/** Plafond de la rente hebdomadaire : **5 %** du pic (§6.3, fourchette PLAN « 2-5 % »). */
export const RENTE_HEBDO_PLAFOND = 0.05;
/** L'XP de rente vaut **×1,5** (§9.1, PLAN §4.6). */
export const RENTE_XP_MULT = 1.5;
/** Rendements décroissants au-delà de **8** evergreens actives : `0,7^(r − 8)` (§6.3). */
export const RENTE_SATURATION_RANG = 8;
export const RENTE_SATURATION_BASE = 0.7;
/**
 * ⟦A-2⟧ Rente pendant une pause annoncée : **80 %**, ramenée à **100 %** par *Lore de chaîne*
 * (avenant A-2 point 6, « Synergies d'arbre »).
 */
export const RENTE_MULT_PAUSE = 0.8;
/**
 * La capacité qui identifie *Lore de chaîne* dans le contenu (`content/skills.json` :
 * `lore_de_chaine` est le seul nœud à débloquer `series_bibliotheque`).
 *
 * Le moteur ne connaît jamais un **id de nœud** — « on n'écrit jamais une règle de contenu en
 * dur dans le moteur » (CLAUDE.md) : il lit des **capacités**, vocabulaire fermé de
 * SPEC-CONTENU §2.3 que `collectModifiers(...).unlocked` expose.
 */
export const CAPACITE_LORE_DE_CHAINE = "series_bibliotheque";
// ─────────────────────────────────────────────────────────────────────────────
// ⟦A-2⟧ La pause annoncée — lectures
// ─────────────────────────────────────────────────────────────────────────────
/** Une pause annoncée est-elle en cours ? (`state.pause_announced`, posé par M1.23) */
export function enPauseAnnoncee(state) {
    return state.pause_announced != null;
}
/**
 * Le gel du silence est-il actif ? Trois conditions cumulatives (avenant A-2) :
 * une pause annoncée en cours · **protégée** (une pause abusive hors fenêtre 90 j ne protège
 * rien, point 5b) · et pas au-delà du **cap de 21 jours simulés** (point 1).
 */
export function gelDuSilenceActif(state, instantH = state.clock_h) {
    const pause = state.pause_announced;
    if (!pause || !pause.protege)
        return false;
    return instantH < pause.since_h + H_PAUSE_PROTEGEE_MAX;
}
/**
 * Heures de pause **protégée** comprises dans l'intervalle `[debutH ; finH]` — la portion de
 * silence que le gel neutralise. C'est ce qui rend le cap de 21 jours honnête : au-delà, le
 * silence recommence à compter **à partir de là**, sans rattrapage rétroactif de la pause.
 */
export function heuresGelees(state, debutH, finH) {
    const pause = state.pause_announced;
    if (!pause || !pause.protege)
        return 0;
    const debut = Math.max(pause.since_h, debutH);
    const fin = Math.min(finH, pause.since_h + H_PAUSE_PROTEGEE_MAX);
    return Math.max(0, fin - debut);
}
/** Le calcul commun aux deux lectures de silence — seul « depuis quand » change. */
function silenceDepuis(state, instantH, depuis) {
    if (instantH <= depuis)
        return 0;
    return Math.max(0, instantH - depuis - heuresGelees(state, depuis, instantH));
}
/**
 * Heures de silence **effectives** à un instant donné : le temps écoulé depuis la dernière
 * publication, moins les heures de pause protégée (⟦A-2⟧).
 *
 * ✎ Avant la toute première publication, `last_publication_h` est `null` : le silence est
 * compté **depuis le début de la run** (`clock_h = 0`), lecture littérale de §2.4 pas 4
 * (`clock_h − last_publication_h`). Conséquence assumée et signalée : un joueur qui traîne
 * plus de 4 jours simulés avant sa première vidéo perd du momentum (plancher 0,8).
 *
 * **Ce que ça sert** : la régularité — decay de momentum (`appliquerDecayMomentum` plus bas),
 * le gain de momentum §5.1 (`publish.ts`) et le decay de Hype (`fil.ts`). Pas le Fantôme —
 * voir `heuresDeSilenceFantome` ⟦A-130 · C12⟧, juste en dessous, et `last_activity_h`
 * (`types.ts`) pour la raison de la séparation.
 */
export function heuresDeSilence(state, instantH = state.clock_h) {
    return silenceDepuis(state, instantH, state.counters.last_publication_h ?? 0);
}
/**
 * ⟦A-130 · C12⟧ Heures de silence lues par le Fantôme **seul** (voie « jamais décollé »,
 * ⟦A-58 · A-79⟧) — depuis `last_activity_h`, pas `last_publication_h`. Une tentative de jalon,
 * réussie ou ratée, avance `last_activity_h` (`jalons.ts`) ; une publication avance les deux
 * (`publish.ts`). Le Fantôme voit donc qu'une chaîne qui tente et rate n'est pas silencieuse,
 * sans que cette tentative ratée ne touche à la régularité que lit `heuresDeSilence`.
 *
 * `last_activity_h` absent (état sérialisé avant ce correctif, ou run qui n'a encore ni publié
 * ni tenté de jalon) ⇒ retombe sur `last_publication_h`, à l'identique de l'ancien
 * comportement — rien à migrer (voir la doc du champ, `types.ts`).
 */
export function heuresDeSilenceFantome(state, instantH = state.clock_h) {
    const depuis = state.counters.last_activity_h ?? state.counters.last_publication_h ?? 0;
    return silenceDepuis(state, instantH, depuis);
}
// ─────────────────────────────────────────────────────────────────────────────
// §4.7 Désabonnements — deux pools, f(Fidélité), pro rata Δh/112
// ─────────────────────────────────────────────────────────────────────────────
/** `taux_hebdo_fideles = 0,0005 + 0,004 × (1 − Fidélité/100)` — 0,05 % → 0,45 %/semaine (§4.7). */
export function tauxDesaboFideles(fidelite) {
    return TAUX_DESABO_BASE + TAUX_DESABO_PENTE * (1 - fidelite / 100);
}
/** `taux_hebdo_volatils = 4 × taux_hebdo_fideles` — 0,2 % → 1,8 %/semaine (§4.7). */
export function tauxDesaboVolatils(fidelite) {
    return FACTEUR_VOLATILS * tauxDesaboFideles(fidelite);
}
/**
 * Perte **fractionnaire** d'abonnés pour un pas de `deltaH` heures éveillées (§4.7) :
 *
 * ```
 * perdus = (fideles × taux_fideles + volatils × taux_volatils) × (Δh / 112)
 * ```
 *
 * `unsubMult` est le `unsub_mult` agrégé du contenu (*Lore de chaîne* et *Figure publique*
 * valent ×0,9 chacun) — on le lit, on ne le recalcule pas.
 */
export function perteDesabonnement(state, deltaH, unsubMult = 1) {
    if (deltaH <= 0)
        return 0;
    const volatils = Math.max(0, Math.min(state.subs_volatile, state.subscribers));
    const fideles = Math.max(0, state.subscribers - volatils);
    const brut = fideles * tauxDesaboFideles(state.loyalty) + volatils * tauxDesaboVolatils(state.loyalty);
    return brut * prorataHebdo(deltaH) * unsubMult;
}
/**
 * Applique les désabonnements de la nuit (§4.7). MUTE `state`.
 *
 * Le report fractionnaire vit dans `counters.frac_unsubs` : une chaîne qui perdrait 0,7 abonné
 * par nuit en perd bien un toutes les deux nuits, au lieu de n'en perdre jamais.
 *
 * ⟦A-2⟧ : ces désabonnements ne sont **jamais gelés** par une pause annoncée. L'avenant gèle
 * « le decay de Momentum et le malus de silence sur la Fidélité » — pas la fuite naturelle,
 * que la table §2.2 impose déjà à l'action `pause` (« désabonnement naturel de la période »).
 */
export function appliquerDesabonnements(state, deltaH, unsubMult = 1, events = []) {
    const accumule = (state.counters.frac_unsubs ?? 0) + perteDesabonnement(state, deltaH, unsubMult);
    const perdus = Math.min(state.subscribers, Math.floor(accumule));
    state.counters.frac_unsubs = accumule - Math.floor(accumule);
    const volatils = Math.min(state.subs_volatile, perdus);
    const fideles = perdus - volatils;
    state.subscribers -= perdus;
    state.subs_volatile -= volatils;
    if (perdus > 0) {
        events.push({
            kind: "subs_lost",
            data: { subs_lost: perdus, volatils, fideles, source: "desabonnement_nuit" },
        });
    }
    return { perdus, volatils, fideles, report: state.counters.frac_unsubs };
}
// ─────────────────────────────────────────────────────────────────────────────
// §6.3 Rente evergreen
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Taux hebdomadaire figé à la publication (ou recalculé au remaster) — §6.3 :
 * **base 2,5 %** × **1,2 si la vidéo est en série**, plafonné à 5 %.
 *
 * ⚠️ **Le ×1,4 du skill SEO n'est PAS figé ici**, contrairement à la lettre de §6.3, et c'est
 * volontaire : `content/skills.json` déclare SEO comme un `multiplier` de cible `rente`
 * (`value: 1.4`), donc `collectModifiers(...).multiplier.rente` le porte déjà. Le figer ici
 * **en plus** le compterait deux fois (1,4² = 1,96). Une seule source de vérité, celle du
 * contenu (même logique que les arbitrages A21/A26) — et le résultat chiffré est identique :
 * 0,025 × 1,4 = 3,5 %, sous le plafond. Écart signalé au rapport.
 */
export function tauxHebdoEvergreen(options = {}) {
    const taux = RENTE_HEBDO_BASE * (options.serie ? RENTE_MULT_SERIE : 1);
    return Math.min(RENTE_HEBDO_PLAFOND, taux);
}
/** Decay de demi-vie 90 jours : `0,5^(age_j / 90)` (§6.3). Rend 0 si la vidéo n'est pas evergreen. */
export function decayEvergreen(video, clockH) {
    if (!video.evergreen)
        return 0;
    return Math.pow(0.5, (clockH - video.evergreen.anchor_h) / H_DEMI_VIE_EVERGREEN);
}
/** Saturation par rang (1-based) : `r ≤ 8 ? 1 : 0,7^(r − 8)` (§6.3). */
export function saturationRente(rang) {
    return rang <= RENTE_SATURATION_RANG
        ? 1
        : Math.pow(RENTE_SATURATION_BASE, rang - RENTE_SATURATION_RANG);
}
/**
 * Rente hebdomadaire **théorique** d'une vidéo à un instant donné, en vues :
 * `vues_pic × weekly_rate × decay`. C'est la clé de tri du §6.3 (« triées par rente_hebdo
 * décroissante ») et la lecture qu'affiche la Bibliothèque.
 */
export function renteHebdoVues(video, clockH) {
    if (!video.evergreen)
        return 0;
    return video.vues_pic * video.evergreen.weekly_rate * decayEvergreen(video, clockH);
}
/**
 * Les evergreens qui touchent une rente, **triées par rente hebdo décroissante** puis par id
 * croissant (le déterminisme en dépend) — §6.3.
 *
 * Trois conditions : classe `evergreen` avec son bloc `evergreen`, **publique** (une vidéo
 * passée en privé ne reçoit plus rien, §6.5) et **courbe initiale épuisée** — §6.2 dit
 * « J+1 → J+14 **puis** rente » : pendant ses 14 premiers jours, une evergreen est payée par sa
 * courbe, pas par sa rente (sinon elle serait payée deux fois, et les accumulateurs
 * fractionnaires `frac_*` du `VideoRecord` seraient partagés par deux versements concurrents).
 */
export function evergreensActives(state) {
    return state.videos
        .filter((v) => v.class === "evergreen" &&
        v.evergreen !== null &&
        v.visibility === "public" &&
        v.remaining_curve.length === 0)
        .sort((a, b) => renteHebdoVues(b, state.clock_h) - renteHebdoVues(a, state.clock_h) || a.id - b.id);
}
/**
 * Tolérance d'arrondi binaire du versement de rente. `1 000 × 0,025 × 1,4` vaut
 * `34,999999999999996` en flottant IEEE-754 : sans cette tolérance, `floor` verserait 34 et
 * le report resterait bloqué à `0,999…` **chaque semaine** — la vidéo perdrait une vue par
 * nuit indéfiniment, exactement ce que « aucun gain ne se perd à l'arrondi » (§1.3) interdit.
 *
 * Purement numérique : 1e-9 vue est neuf ordres de grandeur sous la plus petite quantité que
 * le jeu manipule. Déterministe (arithmétique pure, aucun aléa).
 */
const EPSILON_ARRONDI = 1e-9;
/** Verse un entier depuis un accumulateur fractionnaire (jamais de gain perdu à l'arrondi). */
function prelever(accumulateur) {
    const verse = Math.floor(accumulateur + EPSILON_ARRONDI);
    return { verse, reste: Math.max(0, accumulateur - verse) };
}
/**
 * §2.4 pas 2 / §6.3 — verse la rente de **chaque** evergreen active, pro rata `Δh/112`. MUTE
 * `state` et les `VideoRecord` concernés.
 *
 * `mods.multiplier_etat.rente` est appliqué au versement (le ×1,4 de SEO est déjà FIGÉ dans
 * `weekly_rate` par `publish.ts`, §6.3 ; n'appliquer ici que les multiplicateurs portés par
 * l'état évite de le compter deux fois, tout en laissant expirer un `multiplier`
 * de contenu) ; le plafond de 5 % du pic (§6.3) borne le taux **effectif** ✎, faute de quoi un
 * empilement de multiplicateurs sortirait de la fourchette « 2-5 % du pic » du PLAN.
 *
 * ⟦A-2⟧ Pendant une pause annoncée, la rente tombe à **80 %** — sauf avec *Lore de chaîne*
 * (capacité `series_bibliotheque`), qui la maintient à 100 % (avenant A-2 point 6).
 */
export function verserRente(state, deltaH, mods, events = []) {
    const versees = [];
    if (deltaH <= 0)
        return versees;
    const facteurPause = enPauseAnnoncee(state) && !mods.unlocked.has(CAPACITE_LORE_DE_CHAINE)
        ? RENTE_MULT_PAUSE
        : 1;
    const prorata = prorataHebdo(deltaH);
    let rang = 0;
    for (const video of evergreensActives(state)) {
        rang += 1;
        const tauxEff = Math.min(RENTE_HEBDO_PLAFOND, video.evergreen.weekly_rate * mods.multiplier_etat.rente) *
            facteurPause;
        const brutVues = video.vues_pic * tauxEff * decayEvergreen(video, state.clock_h) * saturationRente(rang) * prorata;
        if (brutVues <= 0)
            continue;
        // — Vues —
        const vues = prelever(video.frac_views + brutVues);
        video.frac_views = vues.reste;
        video.total_views += vues.verse;
        // — Abonnés : TC figé et poids volatil figés à la publication (§4.6-4.7) —
        const volatile = video.format === "short" || bandeAlignement(video.alignment) === "chaos";
        const subs = prelever(video.frac_subs + brutVues * video.taux_conversion);
        video.frac_subs = subs.reste;
        video.total_subs += subs.verse;
        state.subscribers += subs.verse;
        if (volatile)
            state.subs_volatile += subs.verse;
        // — Clicks : couche argent active seulement après le Grand Saut (avenant A-1) —
        let argent = 0;
        if (estMonetise(state)) {
            // ⟦C4⟧ `rpm` et `revenus` étaient deux cibles de `multiplier` **jamais lues** : une
            //   démonétisation de chaîne (×0,3 pendant 240 h, ×0,1 pendant 480 h) ne coûtait rien
            //   au joueur, et le ×1,5 de *L'Empire* — nœud terminal de la branche Business, 12 PC —
            //   n'avait aucun effet. Les deux s'appliquent ici et dans `curves.verserTranche`.
            const rpm = (video.format === "short" ? RPM_SHORT : RPM_LONGUE) * mods.multiplier.rpm;
            const acc = prelever(video.frac_money + (brutVues * rpm * mods.multiplier.revenus) / 1000);
            video.frac_money = acc.reste;
            state.money += acc.verse;
            argent = acc.verse;
        }
        else {
            video.frac_money = 0;
        }
        // — XP de rente : vues × rétention × 1,5 (§9.1) — ⟦C4⟧ cible `xp` (nœud *Cliffhanger*).
        const xp = prelever(video.frac_xp + brutVues * retention(video.qualite_eff) * RENTE_XP_MULT * mods.multiplier.xp);
        video.frac_xp = xp.reste;
        state.xp += xp.verse;
        const versee = {
            video_id: video.id,
            rang,
            vues: vues.verse,
            abonnes: subs.verse,
            argent,
            xp: xp.verse,
        };
        versees.push(versee);
        if (vues.verse > 0 || subs.verse > 0) {
            events.push({ kind: "rent_paid", data: { ...versee } });
        }
    }
    return versees;
}
// ─────────────────────────────────────────────────────────────────────────────
// §2.4 pas 4 — decay de momentum du silence (⟦A-2⟧ gelable)
// ─────────────────────────────────────────────────────────────────────────────
/** Jours entiers de silence **facturables** (au-delà des 3 tolérés) à un instant donné. */
function joursFacturables(state, instantH) {
    const heures = heuresDeSilence(state, instantH);
    if (heures <= H_SILENCE_TOLERE)
        return 0;
    return Math.max(0, Math.floor(heures / H_JOUR) - JOURS_SILENCE_TOLERES);
}
/**
 * §2.4 pas 4 — `momentum −= 0,05 × (jours entiers de silence au-delà de 3)` **depuis la
 * dernière application**. MUTE `state` ; rend le delta appliqué (≤ 0).
 *
 * « Depuis la dernière application » est calculé sans compteur supplémentaire : on facture la
 * différence entre les jours facturables **maintenant** et ceux qui l'étaient déjà au sommeil
 * précédent. Une publication entre les deux remet l'origine du silence à zéro, donc la dette
 * aussi — c'est exactement la sémantique voulue.
 *
 * ⟦A-2⟧ Une pause annoncée protégée gèle le silence (cap 21 jours simulés) : les heures gelées
 * sont retirées de `heuresDeSilence`, si bien qu'au-delà du cap le silence reprend **là où il
 * en est** — jamais de rattrapage rétroactif de la pause entière.
 */
export function appliquerDecayMomentum(state, deltaH) {
    const maintenant = state.clock_h;
    const precedent = Math.max(0, maintenant - Math.max(0, deltaH));
    const aFacturer = joursFacturables(state, maintenant) - joursFacturables(state, precedent);
    if (aFacturer <= 0)
        return 0;
    const delta = -MOMENTUM_DECAY_PAR_JOUR * aFacturer;
    state.momentum += delta;
    return delta;
}
// ─────────────────────────────────────────────────────────────────────────────
// ⟦A-15 §5⟧ L'érosion du silence — la Fidélité et la Réputation suivent le momentum
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦A-15 §5⟧ **Fidélité perdue par jour entier de silence** au-delà des 3 tolérés ✎.
 *
 * **Le constat qui l'ouvre**, et il vaut d'être écrit ici : presque tout ce qui avance avec le
 * temps dans ce moteur joue **en faveur** du joueur — les courbes versent, la rente tombe, les
 * strikes expirent au bout de 90 jours, les thèmes épuisés se libèrent. Ce qui le pénalise
 * tenait en trois lignes : les désabonnements, le decay de momentum, et les charges (qui
 * n'existent qu'après le Grand Saut). Le temps n'était pas gratuit dans l'arc 1 parce qu'il
 * ne s'y passait rien : **il l'était parce que ce qui s'y passait était bon pour le joueur.**
 *
 * ✎ **0,4 point par jour** : une chaîne muette une semaine perd ~1,6 point de Fidélité (4 jours
 * facturables), un mois ~10,8. C'est lent — et ça doit l'être : la Fidélité pilote les
 * désabonnements (§4.7), donc l'érosion se **compose** avec eux. Une pente forte fabriquerait
 * une spirale, exactement ce que le plancher de `MalusNulles` vient de corriger ailleurs.
 */
export const FIDELITE_EROSION_PAR_JOUR = 0.4; // ✎
/**
 * ⟦A-15 §5⟧ **Réputation perdue par jour entier de silence** au-delà des 3 tolérés ✎.
 *
 * ✎ **0,2 point par jour**, soit la moitié de la Fidélité. La Réputation est la jauge de
 * l'**algorithme et des pairs**, pas de la communauté : on est oublié plus lentement qu'on
 * n'est déserté. Elle est aussi la porte du game over `spirale_reputation` (§11.3), d'où une
 * pente délibérément plus douce — l'érosion doit se voir, jamais tuer à elle seule.
 */
export const REPUTATION_EROSION_PAR_JOUR = 0.2; // ✎
/**
 * ⟦A-15 §5⟧ Érode Fidélité et Réputation au silence. MUTE `state` ; rend les deltas (≤ 0).
 *
 * **Volontairement le décalque exact d'`appliquerDecayMomentum`** — même `joursFacturables`,
 * donc même tolérance de 3 jours, même « depuis la dernière application » sans compteur
 * supplémentaire, et surtout **même gel pendant une pause annoncée protégée** ⟦A-2⟧. Ce
 * dernier point n'est pas un détail : A-2 promet au joueur qu'annoncer sa pause protège sa
 * communauté. Une érosion qui continuerait pendant une pause annoncée **trahirait cette
 * promesse** et viderait la mécanique de son sens.
 *
 * ── L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧) ────────────────────────────────
 *
 * Un joueur qui publie dans les 3 jours ne perd **rien** : `joursFacturables` rend 0, et les
 * deux constantes ci-dessus sont les **seules** lignes à mettre à 0 pour désactiver
 * entièrement la pression — « empiler n'a de sens que si dépiler est trivial » (A-15 §5).
 */
export function appliquerErosionSilence(state, deltaH) {
    const maintenant = state.clock_h;
    const precedent = Math.max(0, maintenant - Math.max(0, deltaH));
    const aFacturer = joursFacturables(state, maintenant) - joursFacturables(state, precedent);
    if (aFacturer <= 0)
        return { fidelite_delta: 0, reputation_delta: 0, jours_factures: 0 };
    const fidelite = -FIDELITE_EROSION_PAR_JOUR * aFacturer;
    const reputation = -REPUTATION_EROSION_PAR_JOUR * aFacturer;
    state.loyalty += fidelite;
    state.reputation += reputation;
    return { fidelite_delta: fidelite, reputation_delta: reputation, jours_factures: aFacturer };
}
// ─────────────────────────────────────────────────────────────────────────────
// §2.4 pas 6 — strikes expirés, thèmes épuisés
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Retire les strikes arrivés à expiration (`counters.strikes` porte des `clock_h`
 * d'expiration, §1.5). MUTE `state` ; rend le nombre de strikes tombés.
 */
export function expirerStrikes(state) {
    const avant = state.counters.strikes.length;
    state.counters.strikes = state.counters.strikes.filter((fin) => state.clock_h < fin);
    return avant - state.counters.strikes.length;
}
/** Vidéos consécutives sur le même thème qui l'épuisent (§2.4, PLAN §4.2). */
export const SEUIL_THEME_EPUISE = 3;
/**
 * Thème épuisé (§2.4, PLAN §4.2) : après **3 vidéos consécutives sur le même thème**, le thème
 * est épuisé pendant **14 jours simulés** (`H_THEME_EPUISE` = 224 h). MUTE `state` ; rend les
 * thèmes fraîchement épuisés.
 *
 * Le compteur de série est remis à zéro : la série est **consommée** par l'épuisement, sans
 * quoi le thème se re-épuiserait tout seul à chaque nuit suivant la fin du cooldown.
 *
 * ℹ️ `publish.ts` (M1.9) applique déjà la même règle **à la publication**, et remet le compteur
 * à zéro de la même façon : cette passe de nuit est donc un no-op tant que ce code existe. Elle
 * est conservée parce que la règle est écrite dans §2.4 (la tâche M1.11 en a la charge) et
 * qu'elle est **idempotente** — jamais de double épuisement.
 */
export function appliquerThemesEpuises(state) {
    const theme = state.counters.last_theme;
    if (theme === null || state.counters.consecutive_same_theme < SEUIL_THEME_EPUISE)
        return [];
    state.counters.theme_cooldowns[theme] = state.clock_h + H_THEME_EPUISE;
    state.counters.consecutive_same_theme = 0;
    return [theme];
}
// ─────────────────────────────────────────────────────────────────────────────
// ⟦C10⟧ Le merch — `money_per_1k_subs_weekly`
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Revenu hebdomadaire du **merch**, versé au pro rata comme la rente (§6.3).
 *
 * ⟦C10⟧ L'effet `money_per_1k_subs_weekly` du nœud *Merch* (valeur 5) était agrégé par
 * `effects.ts` et **lu nulle part** : un nœud de l'arbre acheté en PC ne rapportait pas un
 * Click. C'était l'un des deux nœuds inertes de la branche Business, avec *L'Empire* (dont le
 * ×1,5 de `revenus` est réparé au même endroit que `rpm`).
 *
 * Trois règles héritées, aucune inventée :
 *  - **⟦A-1⟧** la couche argent n'existe qu'après le Grand Saut → `estMonetise` ;
 *  - **§6.3** le pro rata `Δh / H_SEMAINE`, comme la rente ;
 *  - **⟦C4⟧** la cible `revenus` s'applique à *tous* les revenus, merch compris.
 *
 * **Neutre à 0** sans le nœud : aucune ancre ne bouge. MUTE `state` ; rend les Clicks versés.
 */
export function verserMerch(state, deltaH, mods) {
    if (deltaH <= 0 || !estMonetise(state))
        return 0;
    const parSemaine = mods.money_per_1k_subs_weekly * (state.subscribers / 1000);
    const du = parSemaine * prorataHebdo(deltaH) * mods.multiplier.revenus;
    if (du <= 0)
        return 0;
    // Report fractionnaire, comme la rente : sinon une petite chaîne ne toucherait jamais rien.
    const acc = prelever((state.counters.frac_merch ?? 0) + du);
    state.counters.frac_merch = acc.reste;
    state.money += acc.verse;
    return acc.verse;
}
/**
 * Le contenu que la nuit lit pour `collectModifiers` : les skills possédés.
 *
 * `EngineContent` ne porte pas encore `skills` — la tâche **M1.14** l'y ajoutera (c'est écrit
 * dans `types.ts`). En attendant, on le lit **structurellement** : absent ⇒ aucun skill, donc
 * `unsub_mult = 1` et `multiplier.rente = 1`. Aucune règle n'est inventée, aucun champ de type
 * n'est préempté, et le jour où M1.14 pose le champ ce shim continue de fonctionner tel quel.
 */
export function skillsDuContenu(content) {
    const avecSkills = content;
    // ⟦A-11 §4⟧ La boutique voyage avec : les objets permanents portent des `sleep_recovery_add`
    // (lit, rideaux, plante), et c'est précisément la nuit qui les lit.
    return { skills: avecSkills.skills ?? [], boutique: avecSkills.boutique ?? [] };
}
/**
 * §2.4 — la résolution de nuit complète, dans l'ordre strict de la spec. MUTE `state`.
 *
 * Signature volontairement structurelle (`ContexteNuit`) : le `HookContext` de `actions.ts`
 * la satisfait, et les tests peuvent l'appeler sans monter toute la file.
 */
export function resoudreNuit(ctx, deltaH) {
    const { state } = ctx;
    const events = ctx.events ?? [];
    const log = ctx.log ?? [];
    const mods = collectModifiers(state, skillsDuContenu(ctx.content));
    // 1. ⟦A-2⟧ SM ×1,5 pendant une pause annoncée. Le +3 de base est déjà débité par la table
    //    des coûts (§2.2) : on n'ajoute que le supplément, et on le lit de la table elle-même.
    //    ✎ Ce supplément vaut pour TOUTE pause annoncée, y compris abusive : A-2 point 5b ne
    //    retire que « la protection » (le gel de Momentum/Fidélité, « l'audience n'y croit
    //    plus ») — le repos réel du joueur n'est pas une affaire d'audience.
    let smBonus = 0;
    if (enPauseAnnoncee(state)) {
        const smBase = actionCost({ type: "sleep" }, state).mental_delta;
        smBonus = smBase * (MULT_SM_PAUSE_ANNONCEE - 1);
        state.mental += smBonus;
    }
    // 1bis. ⟦A-9⟧ Le **matériel de vie** : ce que la nuit rend en plus (`sleep_recovery_add`).
    //   Même principe que le supplément de pause ci-dessus — le `+80 ⚡ / +3 SM` de base est
    //   déjà débité par la table des coûts (§2.2), on n'ajoute ici que le supplément. Les
    //   clamps du pas 9 (§2.3) s'appliquent ensuite : un meilleur lit ne dépasse pas 100.
    //   **Neutre à 0** tant qu'aucun objet n'est possédé — aucune ancre ne bouge (⟦A-10⟧).
    state.energy += mods.sleepRecovery.energie;
    state.mental += mods.sleepRecovery.sante_mentale;
    // 1ter. ⟦A-11 §4⟧ Les consommables achetés « pour la prochaine nuit » (bouchons en mousse,
    //   nuit à l'hôtel). Ils sont **retirés de la file en même temps qu'appliqués** : une seule
    //   nuit, jamais deux. Un joueur sans boutique a une file vide et ne paie rien de tout ça.
    const consommables = consommerNuitProchaine(state, contenuBoutique(ctx.content));
    state.energy += consommables.energie;
    state.mental += consommables.sante_mentale;
    // ⟦A-16⟧ Les trois postes de la nuit sont mesurés SÉPARÉMENT : « la bibliothèque a
    // rapporté », « des gens sont partis » et « le silence se voit » sont trois histoires
    // différentes, et le joueur a besoin des trois pour comprendre son solde d'abonnés.
    const recap = ctx.recap;
    // 2. Rente evergreen, pro rata Δh/112 (§6.3).
    const rente = recap
        ? recap.mesurer("rente_evergreen", () => verserRente(state, deltaH, mods, events))
        : verserRente(state, deltaH, mods, events);
    // 2bis. ⟦C10⟧ Le merch — même pro rata que la rente, même porte ⟦A-1⟧.
    const merchVerse = verserMerch(state, deltaH, mods);
    // 2ter. ⟦C62 · A-179⟧ Le pic d'abonnés de la nuit : la rente vient de tomber, les désabonnés pas encore.
    ctx.noterPic?.();
    // 3. Désabonnements, deux pools, pro rata Δh/112 (§4.7).
    const desabonnements = recap
        ? recap.mesurer("desabonnements", () => appliquerDesabonnements(state, deltaH, mods.unsub_mult, events))
        : appliquerDesabonnements(state, deltaH, mods.unsub_mult, events);
    // 4. Decay de momentum du silence — gelé par une pause annoncée protégée (⟦A-2⟧).
    const momentumDelta = appliquerDecayMomentum(state, deltaH);
    recap?.ligne("momentum_silence", "momentum", momentumDelta);
    // 4bis. ⟦A-15 §5⟧ Le silence n'érode plus seulement le momentum : une chaîne muette perd
    //   aussi de la Fidélité et de la Réputation. Même tolérance de 3 jours, même gel de pause
    //   annoncée — c'est le décalque du pas 4, pas une seconde horloge.
    const erosion = appliquerErosionSilence(state, deltaH);
    recap?.ligne("momentum_silence", "fidelite", erosion.fidelite_delta);
    recap?.ligne("momentum_silence", "reputation", erosion.reputation_delta);
    // 5. Tendances : phases, rotations, fenêtre d'opportunité (§8).
    const tendances = avancerTendances(state, ctx.content, ctx.seed, deltaH, events);
    // ⟦A-15 §2⟧ + ⟦A-16⟧ Une vague qui change de phase pendant qu'on produit est exactement
    // « ce qu'on est en train de rater » : une ligne narrative par changement.
    for (const c of tendances) {
        recap?.fait("tendance", `${c.trend_id}:${c.raison}:${c.phase}`);
    }
    const fenetreFermee = expirerFenetreOpportunite(state);
    // 6. Strikes expirés et thèmes épuisés.
    const strikesExpires = expirerStrikes(state);
    const themesEpuises = appliquerThemesEpuises(state);
    appliquerClamps(state);
    const resultat = {
        delta_h: deltaH,
        sm_bonus_pause: smBonus,
        merch: merchVerse,
        rente,
        desabonnements,
        momentum_delta: momentumDelta,
        erosion_silence: erosion,
        tendances,
        fenetre_fermee: fenetreFermee,
        strikes_expires: strikesExpires,
        themes_epuises: themesEpuises,
    };
    log.push({ turn: state.turn, clock_h: state.clock_h, famille: "quotidien", text: texteDeNuit(state, resultat) });
    return resultat;
}
/** Le hook `nuit` de `ResolutionHooks` (§2.3) — c'est ce que `resolve` (M1.17) branchera. */
export function nuitHook(ctx, deltaH) {
    resoudreNuit(ctx, deltaH);
}
/** Une ligne de journal, ton complice de l'univers YouTube FR (CLAUDE.md). */
function texteDeNuit(state, r) {
    const bouts = [];
    const vuesRente = r.rente.reduce((a, b) => a + b.vues, 0);
    if (vuesRente > 0) {
        bouts.push(
        // ⟦C60 · lot b⟧ Plus de bibliothèque à l'écran : la rente, c'est tes anciennes vidéos qui tournent seules.
        `tes anciennes vidéos ont tourné toutes seules (+${vuesRente} vue${vuesRente > 1 ? "s" : ""})`);
    }
    if (r.desabonnements.perdus > 0) {
        bouts.push(`${r.desabonnements.perdus} désabonné${r.desabonnements.perdus > 1 ? "s" : ""}`);
    }
    if (r.momentum_delta < 0)
        bouts.push("le silence commence à se voir");
    // ⟦A-15 §5⟧ Prévenir, jamais surprendre : l'érosion se DIT, elle ne se découvre pas sur la
    // jauge trois semaines plus tard.
    if (r.erosion_silence.fidelite_delta < 0) {
        bouts.push(`${r.erosion_silence.jours_factures} jour${r.erosion_silence.jours_factures > 1 ? "s" : ""} sans rien poster — la communauté se disperse`);
    }
    if (r.tendances.some((c) => c.raison === "rotation"))
        bouts.push("une nouvelle tendance monte");
    if (r.themes_epuises.length > 0)
        bouts.push(`thème « ${r.themes_epuises[0]} » usé jusqu'à l'os`);
    if (enPauseAnnoncee(state))
        bouts.push("en pause annoncée — l'audience attend");
    return bouts.length === 0 ? "Nuit calme." : `Nuit : ${bouts.join(", ")}.`;
}
//# sourceMappingURL=sleep.js.map